/* eslint-disable react/jsx-no-constructed-context-values */
import {
	createContext,
	Dispatch,
	PropsWithChildren,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react'

import {
	announcementBannerHeight,
	appBarHeight as appBarHeightDesktop,
	appBarHeightMobile,
} from '@/constants'
import useAnnouncement from '@hooks/useAnnouncement'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { useMediaQuery, useTheme } from '@mui/material'

import { useMe } from './MeProvider'

type LayoutContextValue = {
	sidebarWidth: number
	sidebarExpanded: boolean
	appBarHeight: string
	announcementHeight: string
	tempSidebarOpen: boolean
	setSidebarExpanded: Dispatch<SetStateAction<boolean>>
	setTempSidebarOpen: Dispatch<SetStateAction<boolean>>
	largeScreen: boolean
}

export const LayoutContext = createContext<LayoutContextValue | undefined>(undefined)

/**
 * Provide a context for the layout of the screens.
 */
export default function LayoutProvider({ children }: PropsWithChildren) {
	const theme = useTheme()
	const largeScreen = useMediaQuery(theme.breakpoints.up('sm'))
	const { preferences } = useMe()
	const announcementMessage = useAnnouncement()
	const initialSidebarExpanded = preferences.sidebarExpanded as boolean
	const uiUpdateEnabled = useBoolVariation('feature-art-1553-new-ui-layout', false)

	const [tempSidebarOpen, setTempSidebarOpen] = useState(false)
	const [sidebarExpanded, setSidebarExpanded] = useState(initialSidebarExpanded)

	const expandedSidebarWidth = uiUpdateEnabled ? 200 : 250
	const sidebarWidth = sidebarExpanded ? expandedSidebarWidth : 56

	const appBarHeight = largeScreen ? appBarHeightDesktop : appBarHeightMobile
	const announcementHeight = announcementMessage ? announcementBannerHeight : '0rem'

	// Sets sidebar to be expanded when screen is small and resets tempSidebar state on screen size change
	useEffect(() => {
		if (!largeScreen) {
			setSidebarExpanded(true)
			setTempSidebarOpen(false)
		} else {
			setSidebarExpanded(initialSidebarExpanded)
			setTempSidebarOpen(false)
		}
	}, [initialSidebarExpanded, largeScreen])

	return (
		<LayoutContext.Provider
			value={{
				sidebarWidth,
				sidebarExpanded,
				setSidebarExpanded,
				tempSidebarOpen,
				setTempSidebarOpen,
				appBarHeight,
				announcementHeight,
				largeScreen,
			}}
		>
			{children}
		</LayoutContext.Provider>
	)
}

export const useLayout = () => {
	const layoutContext = useContext(LayoutContext)
	if (layoutContext === undefined) {
		throw new Error('useLayout must be inside a LayoutContext')
	}

	return layoutContext
}
